import { useState } from 'react';

export default function Features() {
  const [flipped, setFlipped] = useState({});

  const toggleFlip = (id) => {
    setFlipped(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const features = [
    {
      id: 1,
      icon: "💳",
      title: "Easypaisa & JazzCash",
      short: "Reach 95% of Pakistan",
      detail: "Accept payments from Pakistan’s top mobile wallets. Redirect or H2H API. Auto-verified via secure webhooks. D0 settlement guaranteed."
    },
    {
      id: 2,
      icon: "🏦",
      title: "Bank Transfers",
      short: "IBFT, Raast, 1-Link",
      detail: "Support all major banks. Real-time status updates. Idempotency keys prevent duplicates. Full reconciliation logs."
    },
    {
      id: 3,
      icon: "🔗",
      title: "H2H API",
      short: "Server-to-Server",
      detail: "No redirects. Full control in your backend. JSON responses. Idempotency + webhook confirmations. Ideal for apps."
    },
    {
      id: 4,
      icon: "⚡",
      title: "D0 Settlement",
      short: "Same-Day USDT",
      detail: "Funds in your TRC20 wallet the same day. No T+1 delays. Binance official rate used for every conversion."
    },
    {
      id: 5,
      icon: "📊",
      title: "Live Dashboard",
      short: "Real-Time Analytics",
      detail: "Track volume, success rates, and payouts. Filter by date, method, or status. Export CSV. Dark mode ready."
    },
    {
      id: 6,
      icon: "🔑",
      title: "API Key Management",
      short: "Secure & Flexible",
      detail: "Create, rotate, revoke keys. Scope permissions. View usage logs. Prevent breaches with IP whitelisting."
    },
    {
      id: 7,
      icon: "🔔",
      title: "Signed Webhooks",
      short: "Instant Notifications",
      detail: "HMAC-signed payloads. Retry queue. Dashboard tester. Trigger workflows on success, failure, or refund."
    },
    {
      id: 8,
      icon: "🌍",
      title: "USDT TRC20",
      short: "Lowest Fees",
      detail: "TRON network = < $0.01 fees. Fast confirmations. Multi-wallet support. Cold + hot wallet treasury."
    },
    {
      id: 9,
      icon: "📈",
      title: "Binance Rate",
      short: "Fair Pricing",
      detail: "Official Binance USDT/PKR rate. Timestamped logs. No spreads. Full audit trail for compliance."
    },
    {
      id: 10,
      icon: "🛡️",
      title: "KYC Portal",
      short: "One-Click Compliance",
      detail: "Upload CNIC, NTN, business docs. Automated verification. Status tracking. SECP + SBP ready."
    },
    {
      id: 11,
      icon: "🔄",
      title: "Manual Reissue",
      short: "Fix Failed Payouts",
      detail: "Admin panel to retry or refund. Full history. Email alerts. Prevent merchant complaints."
    },
    {
      id: 12,
      icon: "📱",
      title: "Mobile SDKs",
      short: "React Native & Flutter",
      detail: "Pre-built components. One-line integration. Theming support. Test mode. Live in app stores."
    },
  ];

  return (
    <div className="pt-32 pb-20 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="container mx-auto px-6">
        <div className="text-center mb-20">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 font-poppins bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            Everything You Need to Grow
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto">
            One gateway. All local rails. Global crypto payouts. Built for Pakistani merchants.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {features.map((feature) => (
            <div
              key={feature.id}
              className="relative h-80 preserve-3d cursor-pointer group"
              onClick={() => toggleFlip(feature.id)}
              onMouseEnter={() => !window.matchMedia("(pointer: coarse)").matches && toggleFlip(feature.id)}
              onMouseLeave={() => !window.matchMedia("(pointer: coarse)").matches && toggleFlip(feature.id)}
            >
              {/* Flip Container */}
              <div
                className={`absolute inset-0 w-full h-full transition-all duration-700 transform-gpu ${
                  flipped[feature.id] ? 'rotate-y-180' : ''
                }`}
                style={{ transformStyle: 'preserve-3d' }}
              >
                {/* Front */}
                <div className="absolute inset-0 backface-hidden bg-white rounded-3xl shadow-xl p-8 flex flex-col items-center justify-center text-center hover:shadow-2xl transition-shadow">
                  <div className="text-7xl mb-6 group-hover:scale-110 transition-transform">
                    {feature.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 font-poppins">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {feature.short}
                  </p>
                  <p className="mt-4 text-xs text-primary font-semibold">
                    {window.matchMedia("(pointer: coarse)").matches ? 'Tap' : 'Hover'} to see details →
                  </p>
                </div>

                {/* Back */}
                <div className="absolute inset-0 backface-hidden rotate-y-180 bg-gradient-to-br from-primary to-accent rounded-3xl shadow-2xl p-8 flex flex-col justify-center text-white">
                  <div className="text-5xl mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-bold mb-4 font-poppins">
                    {feature.title}
                  </h3>
                  <p className="text-sm leading-relaxed">
                    {feature.detail}
                  </p>
                  <div className="mt-6">
                    <span className="inline-block px-4 py-2 bg-white/20 rounded-full text-xs font-semibold backdrop-blur">
                      Powered by TecPay
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-20">
          <a
            href="/docs"
            className="inline-flex items-center gap-3 bg-gradient-to-r from-primary to-accent text-white px-10 py-5 rounded-xl text-xl font-bold hover:shadow-2xl hover:scale-105 transition-all"
          >
            Explore API Docs
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>

      {/* CSS for 3D Flip */}
      <style jsx>{`
        .preserve-3d { transform-style: preserve-3d; }
        .backface-hidden { backface-visibility: hidden; }
        .rotate-y-180 { transform: rotateY(180deg); }
      `}</style>
    </div>
  );
}